import React, { useEffect, useState } from 'react';
import { Loader2, Scale, X } from 'lucide-react';
import { useJyotishaLanguage } from '../jyotisha/JyotishaLanguageContext';
import { JyotishaEthicsContent } from '../jyotisha/JyotishaEthicsContent';
import { LEARNING_CONSENT_COPY } from '../../constants/jyotishaLearningConsent';
import { Overlay } from 'components/ui/Overlay';

/**
 * Ethics + learning consent overlay. Required on first sign-in; optional from profile menu.
 */
export function JyotishaEthicsModal({
  isDark,
  open,
  required = false,
  user = null,
  hasConsent = false,
  busy = false,
  error = '',
  onClose,
  onAcceptConsent,
  onSignOut,
}) {
  const { showEnglish } = useJyotishaLanguage();
  const [checked, setChecked] = useState(false);
  const c = LEARNING_CONSENT_COPY;

  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  const shell = isDark
    ? 'border border-amber-400/35 bg-gradient-to-br from-amber-950/95 via-yellow-950/90 to-orange-950/95 text-amber-50'
    : 'border border-amber-300/70 bg-gradient-to-br from-amber-50 via-yellow-50/95 to-orange-50 text-amber-950';

  const muted = isDark ? 'text-amber-200/80' : 'text-amber-800/85';
  const showConsentUi = required || Boolean(user);
  const canAccept = Boolean(user) && !hasConsent;
  const alreadyAccepted = Boolean(user) && hasConsent;

  const title =
    showConsentUi
      ? showEnglish
        ? 'Ethics and consent'
        : 'नैतिकता और सहमति'
      : showEnglish
        ? 'Ethics'
        : 'नैतिकता';

  const acceptLabel = alreadyAccepted
    ? showEnglish
      ? 'Consent recorded'
      : 'सहमति दर्ज है'
    : showEnglish
      ? c.acceptEn
      : c.acceptHi;

  return (
    <Overlay
      open={open}
      onClose={required ? undefined : onClose}
      closeOnBackdrop={!required}
      blockEscape={required}
      zIndexClass="z-[10060]"
      backdropClassName="bg-black/70 backdrop-blur-sm"
      aria-labelledby="jyotisha-ethics-modal-title"
      backdropAriaLabel={showEnglish ? 'Close ethics dialog' : 'नैतिकता संवाद बंद करें'}
    >
      <div
        className={`flex max-h-[min(92dvh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl shadow-2xl sm:rounded-2xl ${shell}`}
      >
        <header className="flex shrink-0 items-center gap-2.5 border-b border-amber-400/25 px-4 py-3">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
              isDark ? 'bg-amber-400/18 text-amber-100' : 'bg-amber-200/80 text-amber-950'
            }`}
            aria-hidden
          >
            <Scale className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id="jyotisha-ethics-modal-title"
              className={`text-base font-bold leading-snug ${showEnglish ? '' : 'font-devanagari'}`}
              lang={showEnglish ? 'en' : 'hi'}
            >
              {title}
            </h2>
            {required ? (
              <p className={`text-[11px] ${muted} ${showEnglish ? '' : 'font-devanagari'}`} lang={showEnglish ? 'en' : 'hi'}>
                {showEnglish ? 'Read this, then accept to continue.' : 'पढ़ें, फिर जारी रखने के लिए स्वीकार करें।'}
              </p>
            ) : null}
          </div>
          {!required ? (
            <button
              type="button"
              onClick={() => onClose?.()}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                isDark ? 'text-amber-100/80 hover:bg-white/10' : 'text-amber-900/70 hover:bg-amber-100'
              }`}
              aria-label={showEnglish ? 'Close' : 'बंद करें'}
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
          ) : null}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
          <JyotishaEthicsContent isDark={isDark} />
        </div>

        {showConsentUi ? (
          <footer className="shrink-0 space-y-3 border-t border-amber-400/25 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
            <label
              className={`flex items-start gap-2.5 rounded-xl border p-3 ${
                canAccept ? 'cursor-pointer' : 'cursor-default'
              } ${isDark ? 'border-white/12 bg-black/20' : 'border-amber-200/90 bg-white/75'}`}
            >
              <input
                type="checkbox"
                checked={alreadyAccepted || checked}
                disabled={!canAccept}
                onChange={(e) => {
                  if (canAccept) setChecked(e.target.checked);
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded accent-amber-500 disabled:opacity-70"
              />
              <span className={`text-xs leading-relaxed sm:text-sm ${showEnglish ? '' : 'font-devanagari'}`} lang={showEnglish ? 'en' : 'hi'}>
                {showEnglish ? c.checkboxEn : c.checkboxHi}
              </span>
            </label>

            {alreadyAccepted ? (
              <p className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'} ${showEnglish ? '' : 'font-devanagari'}`} lang={showEnglish ? 'en' : 'hi'}>
                {showEnglish ? 'You already accepted. Thank you.' : 'पहले ही स्वीकार किया। धन्यवाद।'}
              </p>
            ) : null}

            {error ? (
              <p className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                disabled={!canAccept || !checked || busy || alreadyAccepted}
                onClick={() => onAcceptConsent?.()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-violet-500 to-rose-500 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                {acceptLabel}
              </button>

              {required ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSignOut?.()}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold sm:shrink-0 ${
                    isDark ? 'border-white/15 text-gray-300 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {showEnglish ? c.signOutEn : c.signOutHi}
                </button>
              ) : null}

              {!required ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onClose?.()}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold sm:shrink-0 ${
                    isDark ? 'border-white/15 text-amber-100/90 hover:bg-white/5' : 'border-amber-200 text-amber-900 hover:bg-amber-50'
                  }`}
                >
                  {showEnglish ? 'Close' : 'बंद करें'}
                </button>
              ) : null}
            </div>
          </footer>
        ) : (
          <footer className="shrink-0 border-t border-amber-400/25 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
            <button
              type="button"
              onClick={() => onClose?.()}
              className={`w-full rounded-xl border px-4 py-2.5 text-sm font-semibold ${
                isDark ? 'border-white/15 text-amber-100/90 hover:bg-white/5' : 'border-amber-200 text-amber-900 hover:bg-amber-50'
              } ${showEnglish ? '' : 'font-devanagari'}`}
              lang={showEnglish ? 'en' : 'hi'}
            >
              {showEnglish ? 'Close' : 'बंद करें'}
            </button>
          </footer>
        )}
      </div>
    </Overlay>
  );
}
